import { CalendarDate, CalendarDateTime, fromDate, getLocalTimeZone, Time } from '@internationalized/date';

/**
 * Shared date adapters for the react-aria date components (`DatePicker`, `DatePickerActions`,
 * `DatePickerInline`). They keep `@internationalized/date` strictly internal: the components'
 * public contract is always plain JS `Date`.
 */

export const TIME_ZONE = getLocalTimeZone();

// Read-only text shown in a popover trigger: locale-formatted date, plus HH:mm when `showHour`.
export function formatDisplayValue(date: Date | null, showHour: boolean, locale: string): string {
  if (!date) return '';
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  if (showHour) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hourCycle = 'h23';
  }
  return new Intl.DateTimeFormat(locale, options).format(date);
}

// Normalizes the public `value` (Date | string | null) into a JS Date or null.
export function toJsDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function jsDateToCalendarDate(date: Date | null): CalendarDate | null {
  if (!date) return null;
  const zoned = fromDate(date, TIME_ZONE);
  return new CalendarDate(zoned.year, zoned.month, zoned.day);
}

export function jsDateToTime(date: Date | null): Time | null {
  if (!date) return null;
  const zoned = fromDate(date, TIME_ZONE);
  return new Time(zoned.hour, zoned.minute);
}

export function combineToJsDate(date: CalendarDate | null, time: Time | null): Date | null {
  if (!date) return null;
  const dateTime = new CalendarDateTime(date.year, date.month, date.day, time?.hour ?? 0, time?.minute ?? 0);
  return dateTime.toDate(TIME_ZONE);
}
